import { create } from 'zustand';
import { api, getToken } from '../lib/api';

export interface ChatSession {
  _id: string;
  title: string;
  model: string;
  totalCostUsd: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  createdAt: string;
  updatedAt: string;
}

export interface Message {
  _id?: string;
  sessionId?: string;
  role: 'user' | 'assistant';
  content: string;
  model?: string;
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
  createdAt?: string;
  pending?: boolean;
}

export interface ModelInfo {
  key: string;
  label: string;
  inputPerM: number;
  outputPerM: number;
  contextWindow: number;
}

export interface Usage {
  used: number;
  limit: number;
  remaining: number;
}

interface ChatState {
  models: ModelInfo[];
  defaultModel: string;
  sessions: ChatSession[];
  currentSessionId: string | null;
  messages: Message[];
  usage: Usage | null;
  streaming: boolean;
  error: string | null;

  loadModels: () => Promise<void>;
  loadUsage: () => Promise<void>;
  loadSessions: () => Promise<void>;
  selectSession: (id: string | null) => Promise<void>;
  createSession: (model: string) => Promise<ChatSession>;
  renameSession: (id: string, title: string) => Promise<void>;
  deleteSession: (id: string) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
}

export const useChatStore = create<ChatState>((set, get) => ({
  models: [],
  defaultModel: 'claude-sonnet-4-5',
  sessions: [],
  currentSessionId: null,
  messages: [],
  usage: null,
  streaming: false,
  error: null,

  async loadModels() {
    const { data } = await api.get('/api/chat/models');
    set({ models: data.models, defaultModel: data.defaultModel });
  },

  async loadUsage() {
    const { data } = await api.get('/api/chat/usage');
    set({ usage: data });
  },

  async loadSessions() {
    const { data } = await api.get('/api/chat/sessions');
    set({ sessions: data.sessions });
  },

  async selectSession(id) {
    if (!id) {
      set({ currentSessionId: null, messages: [] });
      return;
    }
    const { data } = await api.get(`/api/chat/sessions/${id}`);
    set({ currentSessionId: id, messages: data.messages });
  },

  async createSession(model) {
    const { data } = await api.post('/api/chat/sessions', { model });
    set((s) => ({ sessions: [data.session, ...s.sessions] }));
    return data.session;
  },

  async renameSession(id, title) {
    const { data } = await api.patch(`/api/chat/sessions/${id}`, { title });
    set((s) => ({
      sessions: s.sessions.map((sess) => (sess._id === id ? data.session : sess))
    }));
  },

  async deleteSession(id) {
    await api.delete(`/api/chat/sessions/${id}`);
    set((s) => ({
      sessions: s.sessions.filter((sess) => sess._id !== id),
      currentSessionId: s.currentSessionId === id ? null : s.currentSessionId,
      messages: s.currentSessionId === id ? [] : s.messages
    }));
  },

  async sendMessage(content) {
    const state = get();
    const sessionId = state.currentSessionId;
    if (!sessionId) {
      set({ error: 'Keine Session ausgewählt' });
      return;
    }
    set({
      streaming: true,
      error: null,
      messages: [
        ...state.messages,
        { role: 'user', content },
        { role: 'assistant', content: '', pending: true }
      ]
    });

    try {
      const resp = await fetch(`/api/chat/sessions/${sessionId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({ content })
      });

      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${resp.status}`);
      }
      if (!resp.body) throw new Error('Kein Body');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      const handleEvent = (eventName: string, dataStr: string) => {
        const data = JSON.parse(dataStr);
        if (eventName === 'delta') {
          set((s) => {
            const msgs = [...s.messages];
            const last = msgs[msgs.length - 1];
            if (last && last.role === 'assistant') {
              msgs[msgs.length - 1] = { ...last, content: last.content + data.text };
            }
            return { messages: msgs };
          });
        } else if (eventName === 'done') {
          set((s) => {
            const msgs = [...s.messages];
            const last = msgs[msgs.length - 1];
            if (last && last.role === 'assistant') {
              msgs[msgs.length - 1] = {
                ...last,
                pending: false,
                _id: data.id,
                inputTokens: data.inputTokens,
                outputTokens: data.outputTokens,
                costUsd: data.costUsd
              };
            }
            return {
              messages: msgs,
              usage: data.usage,
              sessions: s.sessions.map((sess) =>
                sess._id === sessionId
                  ? {
                      ...sess,
                      title: data.title,
                      totalCostUsd: data.sessionTotalCostUsd,
                      updatedAt: new Date().toISOString()
                    }
                  : sess
              )
            };
          });
        } else if (eventName === 'error') {
          set({ error: data.message });
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let sepIdx;
        while ((sepIdx = buf.indexOf('\n\n')) !== -1) {
          const block = buf.slice(0, sepIdx);
          buf = buf.slice(sepIdx + 2);
          const lines = block.split('\n');
          let eventName = 'message';
          const dataLines: string[] = [];
          for (const line of lines) {
            if (line.startsWith('event: ')) eventName = line.slice(7).trim();
            else if (line.startsWith('data: ')) dataLines.push(line.slice(6));
          }
          if (dataLines.length) {
            try {
              handleEvent(eventName, dataLines.join('\n'));
            } catch (e) {
              console.error('SSE parse error', e);
            }
          }
        }
      }
    } catch (err: any) {
      set((s) => ({
        error: err.message || 'Fehler beim Senden',
        messages: s.messages.filter((m) => !m.pending)
      }));
    } finally {
      set({ streaming: false });
    }
  }
}));
